'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { auditLog } from '@/lib/supabase/audit-logger';

interface MfaStatus {
  enrolled: boolean;
  verified: boolean;
  factors: any[];
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  mfaStatus: MfaStatus;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  enrollMfa: () => Promise<{ qrCode: string; secret: string; factorId: string }>;
  verifyMfaEnrollment: (factorId: string, code: string) => Promise<void>;
  verifyMfa: (factorId: string, code: string) => Promise<void>;
  getMfaFactors: () => Promise<any[]>;
  logActivity: (action: string, details?: Record<string, any>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>({
    enrolled: false,
    verified: false,
    factors: [],
  });
  const supabase = createClient();
  const router = useRouter();

  const refreshMfaStatus = useCallback(async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const totp = factors?.totp ?? [];
      setMfaStatus({
        enrolled: totp.length > 0,
        verified: aal?.currentLevel === 'aal2',
        factors: totp,
      });
    } catch {
      // ignore
    }
  }, [supabase]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) refreshMfaStatus();
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) refreshMfaStatus();
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'analyst',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    await logActivity('signup', { email });
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      await auditLog.login(email, 'failed');
      throw error;
    }
    await auditLog.login(email, 'success');
    await logActivity('login', { email });
    return data;
  };

  // Sign Out
  const signOut = async () => {
    await auditLog.logout(user?.email || user?.id || 'unknown');
    await logActivity('logout', {});
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    router.push('/login');
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Enroll MFA — returns QR code and secret for TOTP app
  const enrollMfa = async (): Promise<{ qrCode: string; secret: string; factorId: string }> => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Otek1-Syber Authenticator',
    });
    if (error) throw error;
    return {
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      factorId: data.id,
    };
  };

  // Verify MFA enrollment (challenge + verify)
  const verifyMfaEnrollment = async (factorId: string, code: string): Promise<void> => {
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (challengeError) throw challengeError;

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (verifyError) {
      await auditLog.mfaFailed(user?.email || user?.id || 'unknown');
      throw verifyError;
    }

    // Update user_profiles mfa_enrolled flag
    if (user) {
      await supabase
        .from('user_profiles')
        .update({ mfa_enrolled: true })
        .eq('id', user.id);
    }

    await auditLog.mfaEnrolled(user?.email || user?.id || 'unknown');
    await logActivity('mfa_enrolled', {});
    await refreshMfaStatus();
  };

  // Verify MFA in session (for login flow)
  const verifyMfa = async (factorId: string, code: string): Promise<void> => {
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (challengeError) throw challengeError;

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (verifyError) {
      await auditLog.mfaFailed(user?.email || user?.id || 'unknown');
      throw verifyError;
    }

    await auditLog.mfaVerified(user?.email || user?.id || 'unknown');
    await logActivity('mfa_verified', {});
    await refreshMfaStatus();
  };

  // Get MFA factors
  const getMfaFactors = async (): Promise<any[]> => {
    const { data } = await supabase.auth.mfa.listFactors();
    return data?.totp ?? [];
  };

  // Log user activity
  const logActivity = async (action: string, details: Record<string, any> = {}): Promise<void> => {
    try {
      const currentUser = user || (await supabase.auth.getUser()).data.user;
      if (!currentUser) return;
      await supabase.from('user_activity_logs').insert({
        user_id: currentUser.id,
        action,
        details,
        ip_address: null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
    } catch {
      // Non-critical — don't throw
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    mfaStatus,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    enrollMfa,
    verifyMfaEnrollment,
    verifyMfa,
    getMfaFactors,
    logActivity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
